import { useCallback, useEffect, useState } from "react";
import { refreshSession } from "../api/tokenService";
import { subscribeAccessToken } from "../utils/authTokenStore";
import { isAuthPath } from "../utils/authPath";
import { saveReturnPath } from "../utils/cacheStorage";

export type SessionStatus = "loading" | "authenticated" | "unauthenticated" | "offline";

// Restores the session on page load from the httpOnly refresh cookie, then keeps the status in
// step with the in-memory access token. An invalid session (401/403) leads to sign-in; a network
// or server failure leads to a retry screen and keeps the session.
export const useSessionBootstrap = () => {
  const [status, setStatus] = useState<SessionStatus>("loading");

  const restoreSession = useCallback(async () => {
    setStatus("loading");
    const result = await refreshSession();
    if (result.status === "ok") {
      setStatus("authenticated");
    } else if (result.status === "invalid") {
      // Remember where the user was so signing in again returns them there (FR-009b). Explicit
      // sign-out never reaches this branch, so it still lands on the default page.
      const { pathname, search } = window.location;
      if (!isAuthPath(pathname)) saveReturnPath(pathname + search);
      setStatus("unauthenticated");
    } else {
      setStatus("offline");
    }
  }, []);

  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  useEffect(
    () =>
      subscribeAccessToken(token => {
        setStatus(previous => {
          if (token) return "authenticated";
          return previous === "loading" || previous === "offline" ? previous : "unauthenticated";
        });
      }),
    []
  );

  return { status, restoreSession };
};
